import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertAction, AlertDescription, Button } from 'prosci-ui';
import { AlertCircle, ArrowRight } from '../../../utils/icons';
import { api } from '../../../utils/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { useSubscriptionModal } from '../../../contexts/SubscriptionModalContext';
import { useHubAppCopy } from '../../../hooks';
import PlanSelector from '../PlanSelector';
import CertificationCheckModal from '../CertificationCheckModal';
import CertificationSuccessModal from '../CertificationSuccessModal';
import CertificationFailureModal from '../CertificationFailureModal';
import CertificationHelpSubmittedModal from '../CertificationHelpSubmittedModal';
import InvoiceRequestModal, { InvoiceBillingData } from '../InvoiceRequestModal';
import { CHECKOUT_CONFIRMATION_STORAGE_KEY } from '../CheckoutConfirmation';
import type { ProrationPreviewResponse } from '../utils/types';

// proration_preview cents fields can arrive as BigDecimal-serialized strings
// (e.g. "49900.0"), so coerce before use — see ../utils/types.ts. Matches the
// Dashboard helper's contract: blank/whitespace strings normalize to null
// (Number('') === 0 would otherwise leak a bogus finite 0).
const coerceFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'string') {
    if (value.trim() === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

interface TierPricing {
  tier: string;
  currency: string;
  amount: number;
  stripe_price_id: string;
  pricing_tier: number;
  requires_certification: boolean;
}

interface PricingData {
  country_code: string;
  country_name: string;
  stripe_region: string;
  pricing_tier: number;
  currency: string;
  publishable_key: string;
  tiers: TierPricing[];
  user_certification_verified: boolean;
  invoice_payment_available: boolean;
  invoice_days_until_due: number;
  vat_collection_enabled: boolean;
  trial_eligible: boolean;
  trial_period_days: number;
}

interface Subscription {
  id: string;
  status: string;
  tier: string;
  pending_plan_change?: boolean;
  pending_tier?: string;
  pending_change_effective_date?: string;
  payment_method_type?: 'card' | 'invoice';
}

interface GrantedMembership {
  id: string;
  type: 'granted';
  tier: string;
  status: 'granted';
  current_period_end: string | null;
  expiration_date: string | null;
  days_remaining: number | null;
  can_convert: boolean;
  product_id: number;
  user_product_id: number;
  discount_eligible?: boolean;
}

interface ApiErrorData {
  message?: string;
  error?: string;
  certification_required?: boolean;
  hosted_invoice_url?: string;
  pending_update_expires_at?: string;
}

interface ApiErrorLike {
  response?: ApiErrorData & {
    data?: ApiErrorData;
  };
}

export const SubscriptionModalContent: React.FC = () => {
  const { user } = useAuth();
  const { closeModal, selectedTier: contextTier, mode } = useSubscriptionModal();
  const navigate = useNavigate();
  const hubAppCopy = useHubAppCopy();
  const copy = hubAppCopy('portalCopy')?.subscriptionCopy;

  const [billingCountry, setBillingCountry] = useState('');
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [selectedTier, setSelectedTier] = useState(contextTier || 'B');
  const [isProsciCertified, setIsProsciCertified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [, setCheckingCertification] = useState(true);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [grantedMembership, setGrantedMembership] = useState<GrantedMembership | null>(null);
  const [allowedTiers, setAllowedTiers] = useState<string[]>(['A', 'B', 'C']);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const [invoiceVatError, setInvoiceVatError] = useState('');
  // Hosted Stripe invoice for an upgrade whose payment failed; paying it applies the tier
  // change. forError pins the link to the message it came with, so an unrelated error
  // raised later in the same modal session doesn't inherit it.
  const [paymentRecovery, setPaymentRecovery] = useState<{
    url: string;
    forError: string;
    expiresAt?: string;
  } | null>(null);

  // Early-adopter discount figures for the invoice modal, fetched from
  // proration_preview so the displayed price matches the invoiced amount.
  const [invoiceDiscount, setInvoiceDiscount] = useState<{
    discountPercentage: number;
    currency: string;
    originalAmountCents: number | null;
    discountedAmountCents: number | null;
    savingsAmountCents: number | null;
  } | null>(null);
  const [invoiceDiscountLoading, setInvoiceDiscountLoading] = useState(false);

  // Modal state
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [showHelpSubmittedModal, setShowHelpSubmittedModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [pendingTier, setPendingTier] = useState<string | null>(null);
  const [invoicePendingTier, setInvoicePendingTier] = useState<string | null>(null);
  const [certificationData, setCertificationData] = useState<{
    firstName: string;
    lastName: string;
    alternateEmail?: string;
    matchedUserName?: string;
  } | null>(null);

  useEffect(() => {
    if (!user) return;

    const pricingFailedErrorText =
      copy?.modal?.errors?.pricingFailed || 'Unable to load pricing information. Please try again.';

    // Initialize pricing, certification, and subscription fetch in parallel
    const initialize = async () => {
      // Fetch pricing data (uses user's billing_country or GeoIP detection)
      try {
        const response = await api.getSubscriptionPricing();
        setPricingData(response.data);
        setBillingCountry(response.data.country_code);
        // Set initial certification status from pricing response
        setIsProsciCertified(response.data.user_certification_verified);
      } catch (err) {
        console.error('[SubscriptionModal] Failed to fetch pricing:', err);
        setError(pricingFailedErrorText);
      } finally {
        setLoadingPricing(false);
      }

      // Fetch current subscription if exists
      try {
        const response = await api.getUserSubscriptions(user.id);
        // API returns { subscriptions: [...], granted_membership: ..., allowed_tiers: [...] }
        const responseData = response.data;
        const subscriptions = (responseData?.subscriptions || responseData || []) as Subscription[];
        // Include 'past_due' status - users with overdue invoices still have a subscription
        const activeSubscription = subscriptions.find((sub) => sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due');
        setCurrentSubscription(activeSubscription ?? null);

        // Extract granted membership and allowed tiers for conversion flow
        if (responseData?.granted_membership) {
          setGrantedMembership(responseData.granted_membership as GrantedMembership);
        }
        if (responseData?.allowed_tiers) {
          setAllowedTiers(responseData.allowed_tiers as string[]);
        }
      } catch (err) {
        console.error('Failed to fetch subscriptions:', err);
      } finally {
        setLoadingSubscription(false);
      }

      // Run certification check in the background (validates against Credly, etc.)
      // This runs async so by the time user clicks a plan, we know their status
      try {
        const certResponse = await api.checkCertification();
        if (certResponse.data.certified) {
          setIsProsciCertified(true);
        }
      } catch (err) {
        console.error('[SubscriptionModal] Background certification check failed:', err);
        // Non-fatal - we'll still use the initial value from pricing response
      } finally {
        setCheckingCertification(false);
      }
    };

    initialize();
  }, [user, copy?.modal?.errors?.pricingFailed]);

  // Update selected tier when context tier changes
  useEffect(() => {
    if (contextTier && ['A', 'B', 'C'].includes(contextTier.toUpperCase())) {
      setSelectedTier(contextTier.toUpperCase());
    }
  }, [contextTier]);

  const handleCertificationRequired = (tier: string) => {
    setPendingTier(tier);
    setShowCheckModal(true);
  };

  const handleCertificationCheck = async (data: { firstName: string; lastName: string; alternateEmail?: string }) => {
    setModalLoading(true);
    setCertificationData(data);

    try {
      const response = await api.validateCertificationWithAlternateEmail({
        first_name: data.firstName,
        last_name: data.lastName,
        alternate_email: data.alternateEmail
      });

      if (response.data.validated) {
        console.log('[Certification] ✓ User validated via modal');
        setCertificationData({
          ...data,
          matchedUserName: response.data.matched_user
            ? `${response.data.matched_user.first_name} ${response.data.matched_user.last_name}`
            : undefined
        });
        setShowCheckModal(false);
        setShowSuccessModal(true);
      } else {
        console.log('[Certification] ✗ Validation failed');
        setShowCheckModal(false);
        setShowFailureModal(true);
      }
    } catch (err: unknown) {
      console.error('[Certification] Validation error:', err);
      const apiErr = err as ApiErrorLike;
      setError(apiErr.response?.data?.message || 'Certification validation failed. Please try again.');
      setShowCheckModal(false);
    } finally {
      setModalLoading(false);
    }
  };

  const handleProceedToCheckout = () => {
    setShowSuccessModal(false);
    if (pendingTier) {
      if (invoicePendingTier) {
        // User selected "Pay by Invoice" before cert verification - show invoice modal
        setShowInvoiceModal(true);
      } else {
        handleCheckout(pendingTier);
      }
    }
  };

  // Handler for pending certification checkout - creates subscription with cancel_at_period_end
  const handleContinueToPaymentPendingCert = async () => {
    if (!user || !pendingTier || !billingCountry) return;

    // If user selected invoice payment, show invoice modal instead of Stripe checkout
    if (invoicePendingTier) {
      setShowHelpSubmittedModal(false);
      setShowInvoiceModal(true);
      return;
    }

    setModalLoading(true);
    setError('');

    try {
      // Cancel URL reopens modal
      const cancelUrl = `${window.location.origin}/?modal=subscription&mode=subscribe&tier=${pendingTier}&cancelled=true`;

      if (grantedMembership) {
        // Converting from granted membership with pending certification
        const flashMessage = copy?.pendingCertification?.flash?.subscriptionActive
          || 'Your subscription is active! We\'re verifying your certification status.';
        const successUrl = `${window.location.origin}/checkout/confirmation?subscriptionSuccess=true&pendingCertification=true&flashMessage=${encodeURIComponent(flashMessage)}`;

        const response = await api.convertGranted({
          tier: pendingTier,
          billing_country: billingCountry,
          success_url: successUrl,
          cancel_url: cancelUrl,
          pending_certification: true
        });

        window.location.href = response.data.checkout_url;
      } else {
        // Standard new subscription with pending certification
        const flashMessage = copy?.pendingCertification?.flash?.trialStarted
          || 'Your trial has started! We\'re verifying your certification status.';
        const successUrl = `${window.location.origin}/checkout/confirmation?subscriptionSuccess=true&pendingCertification=true&flashMessage=${encodeURIComponent(flashMessage)}`;

        const response = await api.createCheckoutSession({
          user_id: user.id,
          tier: pendingTier,
          billing_country: billingCountry,
          success_url: successUrl,
          cancel_url: cancelUrl,
          pending_certification: true
        });

        window.location.href = response.data.checkout_url;
      }
    } catch (err: unknown) {
      console.error('[Certification] Pending cert checkout failed:', err);
      const apiErr = err as ApiErrorLike;
      setError(apiErr.response?.data?.error || apiErr.response?.data?.message || 'Failed to start checkout. Please try again.');
      setModalLoading(false);
    }
  };

  const handleSubmitHelpRequest = async (data: {
    yearCertified: string;
    companyAtTimeOfCertification: string;
    nameOfInstructor: string;
    alternateNames: string;
    uploadedFiles?: File[];
    otherNotes: string;
  }) => {
    if (!certificationData) return;

    setModalLoading(true);

    try {
      // Upload files first and collect URLs
      const attachmentUrls: string[] = [];
      if (data.uploadedFiles && data.uploadedFiles.length > 0) {
        console.log('[Certification] Uploading', data.uploadedFiles.length, 'file(s)...');
        for (const file of data.uploadedFiles) {
          try {
            const uploadResponse = await api.uploadCertificationDocument(file);
            if (uploadResponse.data.url) {
              attachmentUrls.push(uploadResponse.data.url);
              console.log('[Certification] ✓ Uploaded:', file.name);
            }
          } catch (uploadErr) {
            console.error('[Certification] Failed to upload file:', file.name, uploadErr);
            // Continue with other files even if one fails
          }
        }
      }

      const additionalDetails = `
Year Certified: ${data.yearCertified}
Company at time of certification: ${data.companyAtTimeOfCertification}
Name of Instructor: ${data.nameOfInstructor}
Alternate Name(s): ${data.alternateNames}
Other Notes: ${data.otherNotes}
${data.uploadedFiles && data.uploadedFiles.length > 0 ? `\nUploaded Files: ${data.uploadedFiles.map(f => f.name).join(', ')}` : ''}
      `.trim();

      await api.submitCertificationHelpRequest({
        first_name: certificationData.firstName,
        last_name: certificationData.lastName,
        alternate_email: certificationData.alternateEmail,
        additional_details: additionalDetails,
        attachment_urls: attachmentUrls
      });

      console.log('[Certification] ✓ Help request submitted');
      setShowFailureModal(false);
      setShowHelpSubmittedModal(true);
      setError('');

      // Keep pendingTier so user can proceed to checkout with pending certification
      setCertificationData(null);
    } catch (err: unknown) {
      console.error('[Certification] Help request error:', err);
      setError('Failed to submit help request. Please try again or contact support directly.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModals = () => {
    setShowCheckModal(false);
    setShowSuccessModal(false);
    setShowHelpSubmittedModal(false);
    setShowFailureModal(false);
    setShowInvoiceModal(false);
    setPendingTier(null);
    setInvoicePendingTier(null);
    setCertificationData(null);
    setModalLoading(false);
  };

  // When the invoice modal opens for a discount-eligible granted member, pull
  // the same proration_preview figures the dashboard banner uses so the modal
  // can surface the discounted price. The discount itself is applied
  // server-side in invoice_checkout; this is display-only.
  useEffect(() => {
    if (!showInvoiceModal || !grantedMembership?.discount_eligible) {
      setInvoiceDiscount(null);
      setInvoiceDiscountLoading(false);
      return;
    }

    const tier = invoicePendingTier || selectedTier;
    let cancelled = false;
    setInvoiceDiscountLoading(true);

    api
      .getProrationPreview(tier)
      .then((res) => {
        if (cancelled) return;
        const data = res.data as Partial<ProrationPreviewResponse> | undefined;
        if (!data?.discount_eligible) {
          setInvoiceDiscount(null);
          return;
        }
        setInvoiceDiscount({
          discountPercentage: coerceFiniteNumber(data.discount_percentage) ?? 0,
          currency: data.currency || 'USD',
          originalAmountCents: coerceFiniteNumber(data.tier_price_cents),
          discountedAmountCents: coerceFiniteNumber(data.first_payment_cents),
          savingsAmountCents: coerceFiniteNumber(data.discount_cents),
        });
      })
      .catch(() => {
        if (!cancelled) setInvoiceDiscount(null);
      })
      .finally(() => {
        if (!cancelled) setInvoiceDiscountLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showInvoiceModal, grantedMembership, invoicePendingTier, selectedTier]);

  // Invoice payment flow handlers
  const handleInvoiceRequest = (tier: string) => {
    // Check if certification is required for this tier
    const tierPricing = pricingData?.tiers.find(t => t.tier === tier);
    if (tierPricing?.requires_certification && !isProsciCertified) {
      // Store that we want invoice checkout after certification
      setPendingTier(tier);
      setInvoicePendingTier(tier);
      setShowCheckModal(true);
      return;
    }

    // No certification required or already certified - show invoice modal
    setInvoicePendingTier(tier);
    setShowInvoiceModal(true);
  };

  const handleInvoiceCheckout = async (billingData: InvoiceBillingData) => {
    if (!user || !invoicePendingTier || !billingCountry) return;

    setModalLoading(true);
    setError('');
    setInvoiceVatError('');

    try {
      const tierPricing = pricingData?.tiers.find(t => t.tier === invoicePendingTier);
      const requiresCertification = tierPricing?.requires_certification && !isProsciCertified;

      const response = await api.createInvoiceCheckout({
        user_id: user.id,
        tier: invoicePendingTier,
        billing_country: billingCountry,
        pending_certification: requiresCertification,
        // Include billing data for the invoice
        customer_name: billingData.customerName,
        organization_name: billingData.organizationName || '',
        phone: billingData.phone,
        po_number: billingData.poNumber,
        billing_address: billingData.billingAddress,
        vat_id: billingData.vatId,  // EU B2B reverse charge
      });

      setShowInvoiceModal(false);
      setModalLoading(false);

      const confirmationState = {
        method: 'invoice' as const,
        invoicePdfUrl: response.data.invoice_pdf_url || '',
        dueDate: response.data.subscription?.invoice_due_date || '',
        deferredStartDate: response.data.deferred_start_date || '',
        planName: getPlanName(invoicePendingTier),
        flashMessage: 'Welcome to Prosci Portal! Your subscription is now active.',
      };

      // Persist so a refresh of /checkout/confirmation doesn't lose the
      // invoice data and — more importantly — doesn't bypass the 8-second
      // webhook-settling timer by defaulting to the card path.
      try {
        sessionStorage.setItem(
          CHECKOUT_CONFIRMATION_STORAGE_KEY,
          JSON.stringify(confirmationState)
        );
      } catch {
        // Storage may be unavailable (e.g. Safari private mode); the in-memory
        // router state still carries the payload for the initial render.
      }

      // Navigate first, then close the outer subscription modal overlay.
      navigate('/checkout/confirmation', { state: confirmationState });
      closeModal();
    } catch (err: unknown) {
      console.error('[Invoice] Checkout failed:', err);
      // ApiClientError stores the parsed response body in err.response (not err.response.data)
      const apiErr = err as ApiErrorLike & { message?: string };
      const errorMsg = apiErr.response?.error || apiErr.response?.data?.error || apiErr.message || 'Failed to create invoice subscription. Please try again.';

      if (errorMsg.toLowerCase().includes('vat') || errorMsg.toLowerCase().includes('tax id')) {
        setInvoiceVatError(errorMsg);
        setError('');
      } else {
        setError(errorMsg);
        setInvoiceVatError('');
      }
      setModalLoading(false);
    }
  };

  const handleCheckout = async (tier: string) => {
    if (!user) return;

    if (!billingCountry) {
      setError(copy?.modal?.errors?.billingCountryMissing || 'Unable to determine billing country. Please try again.');
      return;
    }

    setLoading(true);
    setError('');
    setPaymentRecovery(null);

    try {
      if (currentSubscription) {
        // Change plan for existing subscription
        const response = await api.changePlan(currentSubscription.id, tier);
        setCurrentSubscription(response.data as Subscription);

        const isUpgrade = getTierValue(tier) > getTierValue(currentSubscription.tier);
        const planName = getPlanName(tier);
        const upgradeTemplate = copy?.modal?.planChangeSuccess?.upgrade || `Successfully upgraded to {{planName}}! Your new features are now active.`;
        const downgradeTemplate = copy?.modal?.planChangeSuccess?.downgrade || `Your plan will change to {{planName}} at the end of your current billing period.`;
        const message = isUpgrade
          ? upgradeTemplate.replace('{{planName}}', planName)
          : downgradeTemplate.replace('{{planName}}', planName);

        setLoading(false);
        closeModal();

        // Full page navigation with flash message to ensure fresh data and toast shows after load
        window.location.href = `/subscription/manage?flashMessage=${encodeURIComponent(message)}`;
      } else if (grantedMembership) {
        // The API appends the conversion outcome; CheckoutConfirmation turns it into the flash message
        const successUrl = `${window.location.origin}/checkout/confirmation?subscriptionSuccess=true`;
        const cancelUrl = `${window.location.origin}/?modal=subscription&mode=subscribe&tier=${tier}&cancelled=true`;

        const response = await api.convertGranted({
          tier: tier,
          billing_country: billingCountry,
          success_url: successUrl,
          cancel_url: cancelUrl
        });

        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        // New subscription - standard checkout with trial
        const successUrl = `${window.location.origin}/checkout/confirmation?subscriptionSuccess=true&flashMessage=${encodeURIComponent('Welcome to Prosci Portal! Your subscription is now active.')}`;

        // Cancel: Reopen modal with same tier selection
        const cancelUrl = `${window.location.origin}/?modal=subscription&mode=subscribe&tier=${tier}&cancelled=true`;

        const response = await api.createCheckoutSession({
          user_id: user.id,
          tier: tier,
          billing_country: billingCountry,
          success_url: successUrl,
          cancel_url: cancelUrl
        });

        // Redirect to Stripe Checkout (immediate, no loading message needed)
        window.location.href = response.data.checkout_url;
      }
    } catch (err: unknown) {
      console.error('Checkout/Plan change failed:', err);
      const apiErr = err as ApiErrorLike;

      // Handle both Axios-style (response.data) and ApiClientError-style (response directly) error structures
      const errorData: ApiErrorData | undefined = apiErr.response?.data || apiErr.response;

      if (errorData?.certification_required) {
        setError(copy?.modal?.errors?.certificationRequired || 'This tier requires Prosci practitioner certification. Please select Tier A or contact support.');
      } else {
        const errorMessage = errorData?.error || errorData?.message || copy?.modal?.errors?.checkoutFailed || 'Failed to process request. Please try again.';
        setError(errorMessage);
        if (errorData?.hosted_invoice_url) {
          setPaymentRecovery({
            url: errorData.hosted_invoice_url,
            forError: errorMessage,
            expiresAt: errorData.pending_update_expires_at
          });
        }
      }
      setLoading(false);
    }
  };

  const getTierValue = (tier: string) => {
    const values: { [key: string]: number } = { 'A': 1, 'B': 2, 'C': 3 };
    return values[tier] || 0;
  };

  const getPlanName = (tier: string) => {
    return copy?.plans?.[tier]?.name || { 'A': 'Starter', 'B': 'Essentials', 'C': 'Elevate' }[tier] || tier;
  };

  // Format date for pending change display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Parked upgrades expire in ~23 hours, so the deadline needs a time, not just a date.
  const formatDeadline = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (loadingPricing || loadingSubscription) {
    return (
      <div className="subscription-modal-content">
        <div style={{ padding: '3rem', textAlign: 'center' }}>
          <p>{copy?.modal?.loading || 'Loading subscription options...'}</p>
        </div>
      </div>
    );
  }

  // Determine tax-exempt contact email by country
  const TAX_EXEMPT_EMAILS: Record<string, string> = {
    US: 'billing_us@prosci.com',
    CA: 'accounting@prosci.ca',
  };
  const taxExemptEmail = pricingData?.country_code ? TAX_EXEMPT_EMAILS[pricingData.country_code] : undefined;

  // Build location info with interpolation
  const locationInfoTemplate = copy?.modal?.locationInfo || 'Pricing shown for {{countryName}}. Need to change your location? Contact';
  const locationInfo = pricingData?.country_name
    ? locationInfoTemplate.replace('{{countryName}}', pricingData.country_name)
    : '';

  return (
    <div className="subscription-modal-content">
      <div className="subscription-landing__header">
        <h1 className="subscription-landing__title">
          {mode === 'contact'
            ? (copy?.modal?.contactTitle || 'Upgrade Your Subscription')
            : (copy?.modal?.title || 'Choose Your Plan')}
        </h1>
        <p className="subscription-landing__subtitle">
          {mode === 'contact'
            ? (copy?.modal?.contactSubtitle || 'Contact our support team to discuss upgrading your current access.')
            : (copy?.modal?.subtitle || "Pick the plan that's right for you and continue on your change management journey.")}
        </p>

        {pricingData?.country_name && (
          <p className="subscription-landing__location-info">
            {locationInfo}{' '}
            <a href={`mailto:${copy?.modal?.supportEmail || 'support@prosci.com'}`}>{copy?.modal?.supportEmail || 'support@prosci.com'}</a>
          </p>
        )}

        {/* Pending plan change notice */}
        {currentSubscription?.pending_plan_change && currentSubscription?.pending_tier && (
          <Alert variant="neutral" className="mx-auto max-w-xl text-left mt-4">
            <ArrowRight className="h-4 w-4" />
            <AlertDescription>
              {copy?.pendingChange?.message?.replace('{{pendingTier}}', getPlanName(currentSubscription.pending_tier))?.replace('{{effectiveDate}}', currentSubscription.pending_change_effective_date ? formatDate(currentSubscription.pending_change_effective_date) : '') ||
                `Your plan will change to ${getPlanName(currentSubscription.pending_tier)} on ${currentSubscription.pending_change_effective_date ? formatDate(currentSubscription.pending_change_effective_date) : 'your next billing date'}.`}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mx-auto max-w-xl text-left">
            <AlertCircle />
            <AlertDescription>
              {error}
              {paymentRecovery?.forError === error && paymentRecovery.expiresAt && (
                <span>{`Expires ${formatDeadline(paymentRecovery.expiresAt)}.`}</span>
              )}
            </AlertDescription>
            {paymentRecovery?.forError === error && (
              <AlertAction>
                <Button asChild variant="link" size="sm">
                  <a href={paymentRecovery.url} target="_blank" rel="noopener noreferrer">
                    {copy?.modal?.errors?.completePayment || 'Complete payment'}
                  </a>
                </Button>
              </AlertAction>
            )}
          </Alert>
        )}
      </div>

      <PlanSelector
        selectedTier={selectedTier}
        onTierChange={setSelectedTier}
        isProsciCertified={isProsciCertified}
        pricingData={pricingData}
        onCheckout={handleCheckout}
        onCertificationRequired={handleCertificationRequired}
        onInvoiceRequest={currentSubscription ? undefined : handleInvoiceRequest}
        invoicePaymentAvailable={currentSubscription ? false : (pricingData?.invoice_payment_available || false)}
        loading={loading}
        currentSubscription={currentSubscription ?? undefined}
        contactMode={mode === 'contact'}
        isGrantedConversion={!!grantedMembership}
        allowedTiers={allowedTiers}
        trialEligible={pricingData?.trial_eligible ?? true}
        taxExemptEmail={taxExemptEmail}
      />

      {/* Certification Modal Workflow */}
      <CertificationCheckModal
        isOpen={showCheckModal}
        onClose={handleCloseModals}
        onSubmit={handleCertificationCheck}
        loading={modalLoading}
      />

      <CertificationSuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseModals}
        onProceedToCheckout={handleProceedToCheckout}
        matchedUserName={certificationData?.matchedUserName}
        loading={loading}
      />

      <CertificationFailureModal
        isOpen={showFailureModal}
        onClose={handleCloseModals}
        onSubmitHelpRequest={handleSubmitHelpRequest}
        firstName={certificationData?.firstName || ''}
        lastName={certificationData?.lastName || ''}
        alternateEmail={certificationData?.alternateEmail}
        loading={modalLoading}
      />

      <CertificationHelpSubmittedModal
        isOpen={showHelpSubmittedModal}
        onClose={handleCloseModals}
        onContinueToPayment={handleContinueToPaymentPendingCert}
        loading={modalLoading}
      />

      {/* Invoice Payment Modal */}
      <InvoiceRequestModal
        isOpen={showInvoiceModal}
        onClose={handleCloseModals}
        onConfirm={handleInvoiceCheckout}
        loading={modalLoading}
        selectedTier={invoicePendingTier || selectedTier}
        tierPricing={pricingData?.tiers.find(t => t.tier === (invoicePendingTier || selectedTier)) || null}
        currency={pricingData?.currency || 'USD'}
        daysUntilDue={pricingData?.invoice_days_until_due || 30}
        userEmail={user?.email || ''}
        defaultCustomerName={user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}`.trim() : ''}
        defaultOrganizationName={user?.account?.name || ''}
        stripePublishableKey={pricingData?.publishable_key || ''}
        vatCollectionEnabled={pricingData?.vat_collection_enabled}
        googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
        serverError={error}
        vatError={invoiceVatError}
        discountEligible={!!grantedMembership?.discount_eligible && !!invoiceDiscount}
        discountLoading={invoiceDiscountLoading}
        discountPercentage={invoiceDiscount?.discountPercentage}
        discountCurrency={invoiceDiscount?.currency}
        originalAmountCents={invoiceDiscount?.originalAmountCents ?? null}
        discountedAmountCents={invoiceDiscount?.discountedAmountCents ?? null}
        savingsAmountCents={invoiceDiscount?.savingsAmountCents ?? null}
      />
    </div>
  );
};

export default SubscriptionModalContent;
